import {
	DeviceState,
	DeviceStatus,
} from "Infrastructure/Models/Messages/Dto/DeviceStatus";
import { FunctionComponent, useState } from "react";
import InfoWarningsTable from "./InfoWarningsTable";
import InfoWarningsActions from "./InfoWarningActions";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { CloseDialogHandler } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import {
	InfoWarningCreateMessage,
	InfoWarningDeleteByDescriptionMessage,
} from "Infrastructure/Models/Messages/Messages";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import InstanceResponseContent from "../InstanceResponseContent";
import { getNewFormData as getNewSaveFormData, InfoWarningSaveFormData } from "./InfoWarningActionSave";
import { getNewFormData as getNewDeleteFormData, InfoWarningDeleteFormData } from "./InfoWarningActionDelete";

interface Props {
	deviceStatuses: DeviceStatus[] | null;
	deviceStatus?: DeviceStatus | undefined;
	hubConnection: HubConnection | null;
	closeDialog: CloseDialogHandler;
}

const InfoWarningsDialogContent: FunctionComponent<Props> = (props) => {
	const { deviceStatuses, deviceStatus, hubConnection, closeDialog } = props;
	const showSnackbar = useSnackbar();
	const { t } = useResource();

	const [messageResponse, setMessageResponse] = useState<{
		actionName: string;
		requestId?: string | null;
		deviceNames: string[];
	} | null>(null);

	const [saveFormData, setSaveFormData] = useState<InfoWarningSaveFormData>(getNewSaveFormData());
	const [deleteFormData, setDeleteFormData] = useState<InfoWarningDeleteFormData>(getNewDeleteFormData());

	const handleInfoWarningCreate = (
		infoWarningCreateMessage: InfoWarningCreateMessage
	) => {
		if (
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
			return;
		}

		setMessageResponse({
			actionName: "Vytvoření infolišty",
			requestId: infoWarningCreateMessage.requestId,
			deviceNames: infoWarningCreateMessage.filteredDeviceNames,
		});

		hubConnection
			.invoke("infoWarningCreate", infoWarningCreateMessage)
			.catch((error: Error) => {
				showSnackbar(t(Resources.Errors.Hub.RequestError), "error");
				logError(error);
			});
	};

	const handleInfoWarningDelete = (
		infoWarningDeleteMessage: InfoWarningDeleteByDescriptionMessage
	) => {
		if (
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
			return;
		}

		setMessageResponse({
			actionName: "Smazání infolišty",
			requestId: infoWarningDeleteMessage.requestId,
			deviceNames: infoWarningDeleteMessage.filteredDeviceNames,
		});

		hubConnection
			.invoke("infoWarningDeleteByDescription", infoWarningDeleteMessage)
			.catch((error: Error) => {
				showSnackbar(t(Resources.Errors.Hub.RequestError), "error");
				logError(error);
			});
	};

	if (messageResponse) {
		return (
			<InstanceResponseContent
				actionName={messageResponse.actionName}
				requestId={messageResponse.requestId}
				deviceNames={messageResponse.deviceNames}
				hubConnection={hubConnection}
				closeDialog={closeDialog}
			/>
		);
	}

	return (
		<Stack direction="column">
			{deviceStatuses && deviceStatuses?.length > 0 && (
				<InfoWarningsActions
					deviceStatuses={deviceStatuses}
					deviceStatus={deviceStatus}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
					onCreate={handleInfoWarningCreate}
					onDelete={handleInfoWarningDelete}
					setSaveFormData={setSaveFormData}
					setDeleteFormData={setDeleteFormData}
					saveFormData={saveFormData}
					deleteFormData={deleteFormData}
				/>
			)}
			{deviceStatus && (
				<Box mt={1}>
					<Box my={1}>
						<Typography variant="h6" component="div">
							Data
						</Typography>
						<Divider />
					</Box>
					<Paper>
						<InfoWarningsTable
							deviceName={deviceStatus?.deviceName}
							infoWarnings={deviceStatus?.infoWarnings}
							hubConnection={hubConnection}
							closeDialog={closeDialog}
						/>
					</Paper>
				</Box>
			)}
		</Stack>
	);
};

export default InfoWarningsDialogContent;
